import { canvasState } from '@components/MainStage/store';
import { NodesTypes } from '@constants/canvas/general';
import { subLayersNodeIdsByMode } from '@constants/canvas/layerNodes';
import { Modes } from '@constants/canvas/layers';
import { matchesModelFilters } from '@context/ProjectFilters/matchesModelFilters';
import useChangeFilterHandler from '@hooks/useChangeFilterHandler';
import {
  useModelFilterActions,
  useModelFilters,
  withScopeType,
} from '@store/zustand/model-filters';
import { reconcileScopeSelection, ScopeType } from '@utils/domains';

/**
 * The Scope type switch behind the drawer guard every model-filter write
 * carries.
 *
 * The Scope ids that survive are those `reconcileScopeSelection` keeps
 * against the entities of `mode` on the canvas at the moment of the switch,
 * with `alwaysKept` — Enterprise wide and Unassigned — never dropped. The
 * open drawer's entity, or the selected edge's endpoints, is judged against
 * the new type with those ids, and the drawer closes before the canvas hides
 * it. Only then are the type and the ids written, in one store write.
 * Declining the unsaved-changes prompt drops the switch; a switch to the
 * current type writes nothing.
 */
export const useSetScopeType = (
  mode: Modes | null,
  alwaysKept: ReadonlyArray<string>,
) => {
  const changeFilterHandler = useChangeFilterHandler();
  const state = useModelFilters();
  const { setScopeType } = useModelFilterActions();

  return (scopeType: ScopeType): Promise<void> => {
    if (scopeType === state.scopeType) return Promise.resolve();

    const subLayers = mode ? subLayersNodeIdsByMode[mode] ?? [] : [];

    const dtos = canvasState()
      .nodes.filter(
        (node) =>
          node.type === NodesTypes.CustomNode &&
          subLayers.includes(node.parentNode ?? ''),
      )
      .map((node) => node.data?.dto);

    const domains = reconcileScopeSelection(
      state.domains,
      scopeType,
      dtos,
      alwaysKept,
    );

    const next = withScopeType(state, scopeType, domains);

    return changeFilterHandler({
      isNodeVisible: (_values, node) =>
        !node || matchesModelFilters(node, next),
      onSetValues: () => setScopeType(scopeType, domains),
    })(domains).catch(() => undefined);
  };
};
